import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API_URL, getAuthHeaders } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { useApiMessages } from "../hooks/useApiMessages";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Settings, Save, Clock, Calendar, Users, Map, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Valores por defecto para la configuración
const DEFAULT_SETTINGS = {
  overtime_threshold_hours: 8.0,
  allow_holiday_work: true,
  allow_multiple_shifts: true,
  holiday_pay_multiplier: 2.0,
  overtime_pay_multiplier: 1.5,
  map_refresh_interval_seconds: 5,
  preferred_language: null,
};

export default function CompanySettings() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiMessages();
  const { user } = useAuth();
  
  // Estados
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [saving, setSaving] = useState(false);

  // Determinar si el usuario puede gestionar configuraciones
  const canManageSettings = () => {
    return user?.role === "admin" || user?.role === "revendedor" || user?.role === "supervisor";
  };

  // Determinar si el usuario puede seleccionar compañía
  const canSelectCompany = () => {
    return user?.role === "admin" || user?.role === "revendedor";
  };

  // Cargar lista de compañías (solo una vez al montar)
  useEffect(() => {
    const loadCompanies = async () => {
      if (!canManageSettings()) {
        setLoadingCompanies(false);
        return;
      }

      try {
        setLoadingCompanies(true);
        const response = await axios.get(`${API_URL}/companies`, {
          headers: getAuthHeaders(),
        });
        
        const companiesData = Array.isArray(response.data) ? response.data : [];
        setCompanies(companiesData);
        
        // Establecer selectedCompanyId inicial usando función de actualización
        setSelectedCompanyId((prev) => {
          // Si ya hay uno establecido, mantenerlo
          if (prev) return prev;
          
          if (user?.role === "supervisor" && user?.company_id) {
            // Supervisor: usar su company_id
            return user.company_id;
          } else if (companiesData.length > 0) {
            // Admin/Revendedor: usar la primera compañía disponible
            return companiesData[0].id;
          }
          return "";
        });
      } catch (error) {
        console.error("Error al cargar compañías:", error);
        toast.error(getErrorMessage(error, "api.errors.loadError"));
        setCompanies([]);
      } finally {
        setLoadingCompanies(false);
      }
    };

    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar una vez al montar

  // Cargar configuración cuando cambia selectedCompanyId
  useEffect(() => {
    // Si no hay selectedCompanyId, establecer valores por defecto y salir
    if (!selectedCompanyId) {
      setSettings(DEFAULT_SETTINGS);
      setLoadingSettings(false);
      return;
    }

    let cancelled = false;
    let timeoutId = null;
    
    const loadSettings = async () => {
      // Establecer loading ANTES de hacer la petición
      setLoadingSettings(true);
      
      // Timeout de seguridad: si después de 8 segundos no se ha resuelto, forzar loading a false
      timeoutId = setTimeout(() => {
        if (!cancelled) {
          console.warn("Timeout de seguridad: forzando loading a false");
          setLoadingSettings(false);
          setSettings(DEFAULT_SETTINGS);
        }
      }, 8000);
      
      try {
        const response = await axios.get(`${API_URL}/companies/${selectedCompanyId}`, {
          headers: getAuthHeaders(),
          timeout: 5000,
        });
        
        // Verificar si fue cancelado antes de actualizar estado
        if (cancelled) {
          return;
        }
        
        // Limpiar timeout de seguridad
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        
        const company = response.data;
        if (company) {
          setSettings({
            overtime_threshold_hours: company.overtime_threshold_hours ?? DEFAULT_SETTINGS.overtime_threshold_hours,
            allow_holiday_work: company.allow_holiday_work ?? DEFAULT_SETTINGS.allow_holiday_work,
            allow_multiple_shifts: company.allow_multiple_shifts ?? DEFAULT_SETTINGS.allow_multiple_shifts,
            holiday_pay_multiplier: company.holiday_pay_multiplier ?? DEFAULT_SETTINGS.holiday_pay_multiplier,
            overtime_pay_multiplier: company.overtime_pay_multiplier ?? DEFAULT_SETTINGS.overtime_pay_multiplier,
            map_refresh_interval_seconds: company.map_refresh_interval_seconds ?? DEFAULT_SETTINGS.map_refresh_interval_seconds,
            preferred_language: company.preferred_language ?? DEFAULT_SETTINGS.preferred_language,
          });
        } else {
          setSettings(DEFAULT_SETTINGS);
        }
      } catch (error) {
        // Verificar si fue cancelado antes de actualizar estado
        if (cancelled) {
          return;
        }
        
        // Limpiar timeout de seguridad
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        
        // Manejar errores silenciosamente y usar valores por defecto
        if (error.response?.status === 404) {
          console.warn("Compañía no encontrada (404), usando valores por defecto");
        } else if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
          console.warn("Error de red, usando valores por defecto");
        } else {
          console.error("Error al cargar configuración:", error);
          // Solo mostrar toast para errores que no sean 404 o de red
          if (error.response?.status !== 404) {
            toast.error(getErrorMessage(error, "api.errors.loadError"));
          }
        }
        // Usar valores por defecto en caso de error
        setSettings(DEFAULT_SETTINGS);
      } finally {
        // SIEMPRE establecer loading a false si no fue cancelado
        if (!cancelled) {
          setLoadingSettings(false);
        }
      }
    };

    // Ejecutar la carga
    loadSettings();
    
    // Cleanup: cancelar la petición si el componente se desmonta o cambia selectedCompanyId
    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      // Asegurar que el loading se establezca en false al limpiar
      setLoadingSettings(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]); // Solo selectedCompanyId como dependencia

  // Manejar cambio de configuración
  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Guardar configuración
  const handleSave = async () => {
    if (!selectedCompanyId) {
      toast.error(t("pages.companySettings.selectCompany"));
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/companies/${selectedCompanyId}`,
        settings,
        { headers: getAuthHeaders() }
      );
      toast.success(t("pages.companySettings.saveConfig"));
    } catch (error) {
      console.error("Error al guardar configuración:", error);
      toast.error(getErrorMessage(error, "api.errors.saveConfigError"));
    } finally {
      setSaving(false);
    }
  };

  const selectedCompany = companies.find(c => c.id === selectedCompanyId);

  // Si no puede gestionar configuraciones, no mostrar nada
  if (!canManageSettings()) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">
            {t("pages.companySettings.title")}
          </h1>
          <p className="text-slate-600 mt-1">
            {t("pages.companySettings.subtitle")}
          </p>
        </div>
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-slate-500">{t("pages.companySettings.noPermission")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">
            {t("pages.companySettings.title")}
          </h1>
          <p className="text-slate-600 mt-1">
            {t("pages.companySettings.subtitle")}
          </p>
        </div>
      </div>

      {/* Company Selector - Solo para Admin y Revendedor */}
      {canSelectCompany() && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Label className="text-sm font-medium whitespace-nowrap">
                {t("pages.companySettings.company")}
              </Label>
              <Select 
                value={selectedCompanyId} 
                onValueChange={setSelectedCompanyId}
                disabled={loadingCompanies}
              >
                <SelectTrigger className="w-full max-w-md">
                  <SelectValue placeholder={t("pages.companySettings.selectCompany")} />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mostrar nombre de compañía para Supervisor */}
      {user?.role === "supervisor" && selectedCompany && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Label className="text-sm font-medium whitespace-nowrap">
                {t("pages.companySettings.company")}
              </Label>
              <span className="text-lg font-semibold text-slate-900">
                {selectedCompany.name}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {loadingSettings && (
        <Card>
          <CardContent className="p-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto mb-4" />
            <p className="text-slate-500">{t("pages.companySettings.loadingConfig")}</p>
          </CardContent>
        </Card>
      )}

      {/* Settings Cards - Solo mostrar si hay selectedCompanyId y no está cargando */}
      {selectedCompanyId && !loadingSettings && (
        <>
          {/* Horarios y Horas Extras */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                {t("pages.companySettings.schedules")}
              </CardTitle>
              <CardDescription>
                {t("pages.companySettings.schedulesDescription")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="overtime_threshold">
                    {t("pages.companySettings.overtimeThreshold")}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="overtime_threshold"
                      type="number"
                      step="0.5"
                      min="0"
                      value={settings.overtime_threshold_hours}
                      onChange={(e) => handleSettingChange('overtime_threshold_hours', parseFloat(e.target.value) || 0)}
                      className="max-w-xs"
                    />
                    <span className="text-sm text-slate-600">{t("pages.companySettings.hours")}</span>
                  </div>
                  <p className="text-sm text-slate-500">
                    {t("pages.companySettings.overtimeThresholdHint")}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="overtime_multiplier">
                    {t("pages.companySettings.overtimeMultiplier")}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="overtime_multiplier"
                      type="number"
                      step="0.1"
                      min="1"
                      value={settings.overtime_pay_multiplier}
                      onChange={(e) => handleSettingChange('overtime_pay_multiplier', parseFloat(e.target.value) || 1)}
                      className="max-w-xs"
                    />
                    <span className="text-sm text-slate-600">x</span>
                  </div>
                  <p className="text-sm text-slate-500">
                    {t("pages.companySettings.overtimeMultiplierHint")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Días Feriados */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {t("pages.companySettings.holidays")}
              </CardTitle>
              <CardDescription>
                {t("pages.companySettings.holidaysDescription")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="allow_holiday_work">
                    {t("pages.companySettings.allowHolidayWork")}
                  </Label>
                  <p className="text-sm text-slate-500">
                    {t("pages.companySettings.allowHolidayWorkHint")}
                  </p>
                </div>
                <Switch
                  id="allow_holiday_work"
                  checked={settings.allow_holiday_work}
                  onCheckedChange={(checked) => handleSettingChange('allow_holiday_work', checked)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="holiday_multiplier">
                  {t("pages.companySettings.holidayMultiplier")}
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="holiday_multiplier"
                    type="number"
                    step="0.1"
                    min="1"
                    value={settings.holiday_pay_multiplier}
                    onChange={(e) => handleSettingChange('holiday_pay_multiplier', parseFloat(e.target.value) || 1)}
                    className="max-w-xs"
                  />
                  <span className="text-sm text-slate-600">x</span>
                </div>
                <p className="text-sm text-slate-500">
                  {t("pages.companySettings.holidayMultiplierHint")}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Turnos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {t("pages.companySettings.shifts")}
              </CardTitle>
              <CardDescription>
                {t("pages.companySettings.shiftsDescription")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="allow_multiple_shifts">
                    {t("pages.companySettings.allowMultipleShifts")}
                  </Label>
                  <p className="text-sm text-slate-500">
                    {t("pages.companySettings.allowMultipleShiftsHint")}
                  </p>
                </div>
                <Switch
                  id="allow_multiple_shifts"
                  checked={settings.allow_multiple_shifts}
                  onCheckedChange={(checked) => handleSettingChange('allow_multiple_shifts', checked)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Configuración de Mapa - Solo para Admin */}
          {user?.role === "admin" && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Map className="h-5 w-5" />
                  {t("pages.companySettings.liveMap")}
                </CardTitle>
                <CardDescription>
                  {t("pages.companySettings.liveMapDescription")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="map_refresh_interval">
                    {t("pages.companySettings.refreshInterval")}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="map_refresh_interval"
                      type="number"
                      step="1"
                      min="1"
                      max="60"
                      value={settings.map_refresh_interval_seconds}
                      onChange={(e) => handleSettingChange('map_refresh_interval_seconds', parseInt(e.target.value) || 5)}
                      className="max-w-xs"
                    />
                    <span className="text-sm text-slate-600">{t("pages.companySettings.seconds")}</span>
                  </div>
                  <p className="text-sm text-slate-500">
                    {t("pages.companySettings.refreshIntervalHint")}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Idioma Preferido */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                {t("pages.companySettings.preferredLanguage")}
              </CardTitle>
              <CardDescription>
                {t("pages.companySettings.preferredLanguageDescription")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="preferred_language">
                  {t("pages.companySettings.language")}
                </Label>
                <Select
                  value={settings.preferred_language || "default"}
                  onValueChange={(value) => handleSettingChange('preferred_language', value === "default" ? null : value)}
                >
                  <SelectTrigger className="max-w-xs">
                    <SelectValue placeholder={t("pages.companySettings.selectLanguage")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">{t("pages.companySettings.defaultLanguage")}</SelectItem>
                    <SelectItem value="es">🇪🇸 {t("pages.companySettings.spanish")}</SelectItem>
                    <SelectItem value="en">🇬🇧 {t("pages.companySettings.english")}</SelectItem>
                    <SelectItem value="fr">🇫🇷 {t("pages.companySettings.french")}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-slate-500">
                  {t("pages.companySettings.preferredLanguageHint")}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving || !selectedCompanyId}
              size="lg"
              className="gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("pages.companySettings.saving")}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {t("pages.companySettings.saveConfig")}
                </>
              )}
            </Button>
          </div>
        </>
      )}

      {/* Estado vacío - No hay compañía seleccionada */}
      {!selectedCompanyId && !loadingSettings && !loadingCompanies && (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-slate-500">{t("pages.companySettings.noCompanySelected")}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
